import { useState, type ComponentType, type SVGProps } from 'react';
import {
  AudioIcon,
  FileIcon,
  ImageIcon,
  VideoIcon,
  cn,
} from '@playerbase/ui';
import type { FeedbackAttachment } from '@playerbase/types';

import { useAttachmentRefresh } from './use-attachment-refresh.js';

// ---------------------------------------------------------------------------
// One Discord attachment rendered as a clickable chip.
//
// Chips do not preview inline; clicking refreshes the Discord CDN
// signature via the edge function and opens the asset in a new tab.
// Browsers handle the rest (an image renders, an mp4 plays in a tab,
// a generic file prompts a download). That keeps the feed dense and
// avoids the "this row is now 800px tall because someone uploaded a
// screenshot" problem.
//
// Filename is basenamed because uploaders sometimes ship full local
// paths as the attachment name (`c:/Users/.../Screenshot.png`); we
// only want the actual filename in the chip.
// ---------------------------------------------------------------------------

const KIND_ICONS: Record<
  FeedbackAttachment['kind'],
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  image: ImageIcon,
  video: VideoIcon,
  audio: AudioIcon,
  file: FileIcon,
};

function basename(name: string | null): string {
  if (name === null || name.length === 0) return 'attachment';
  // Handle both POSIX (/) and Windows (\) separators. The user-visible
  // bug: Discord clients sometimes set the attachment name to the
  // local clipboard path, which on Windows looks like
  // `c:\Users\edwar\Pictures\Screenshots\Screenshot 2026-05-07.png`
  // (or the forward-slash variant). Both should display as the bare
  // file leaf.
  const idx = Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\'));
  if (idx === -1) return name;
  const tail = name.slice(idx + 1);
  return tail.length > 0 ? tail : name;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
}

interface AttachmentChipProps {
  attachment: FeedbackAttachment;
}

export function AttachmentChip({ attachment }: AttachmentChipProps): JSX.Element {
  const { getRefreshed } = useAttachmentRefresh();
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');

  const label = basename(attachment.name);
  const Icon = KIND_ICONS[attachment.kind];

  const handleClick = async (): Promise<void> => {
    if (state === 'loading') return;
    setState('loading');
    try {
      const fresh = await getRefreshed(attachment.url);
      // 'noopener,noreferrer' so the opened tab can't reach back into
      // the SPA via window.opener.
      window.open(fresh, '_blank', 'noopener,noreferrer');
      setState('idle');
    } catch (err) {
      console.warn('[attachment-chip] refresh failed', err);
      setState('error');
    }
  };

  return (
    <button
      type="button"
      onClick={() => {
        void handleClick();
      }}
      disabled={state === 'loading'}
      // Mirror ReactionPill's chip styling so attachments and
      // reactions sit in the same visual register at the bottom of
      // a row. Error state is tonal rather than blocking - the user
      // can click again.
      className={cn(
        // Fixed 1.5rem (24px) height so every chip in the footer lines
        // up regardless of content. text-xs without leading-none keeps
        // the default 1rem line-height, which gives descenders (g, p,
        // y) the headroom they need - leading-none used to clip the
        // bottoms inside the truncate span's overflow:hidden.
        'inline-flex items-center gap-1 rounded border px-1.5 text-xs transition-colors',
        'min-h-[1.5rem] max-w-[18rem] cursor-pointer',
        state === 'error'
          ? 'border-danger bg-danger-bg text-danger-text'
          : 'border-border bg-surface-2 text-text-muted hover:bg-surface-3',
        state === 'loading' && 'opacity-70',
      )}
      title={attachment.name ?? label}
      aria-label={`Open ${label}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {/* min-w-0 lets the truncate span actually shrink inside the
          flex row; without it the span's natural width would push the
          size span off the end and force it to wrap. */}
      <span className="min-w-0 truncate">{label}</span>
      {/* shrink-0 + whitespace-nowrap keeps "16 KB" on a single line
          even when the filename above is being squeezed into ellipsis. */}
      <span className="shrink-0 whitespace-nowrap tabular-nums text-text-muted/80">
        {formatSize(attachment.size)}
      </span>
      {state === 'loading' && <span className="shrink-0 text-text-muted">…</span>}
      {state === 'error' && <span className="shrink-0">retry</span>}
    </button>
  );
}
